// Unpublished Work © 2025 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect, useDispatch} from 'react-redux';
import MediaQuery, {
    MOBILE_MEDIA_QUERY
} from 'Ui/components/higher-order-components/media-query';
import {
    MultiSelect,
    Stack,
    List,
    AssistanceTooltip,
    Box,
    IconButton,
    useLinkableText,
    Button,
    Typography
} from '@deere/isg.component-library';
import {Divider} from '@mui/material';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {
    IconAdd,
    IconStar,
    IconStarEmpty,
    IconInfoAlert,
    IconRemove
} from '@deere/icons';
import {openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import JobListItem from './job-list-item';
import {
    onLinkClick,
    getSelectedJobs,
    getTotalOperatorCount,
    SelectAllConfirmation
} from '../../utils/setup-workboard';
import {
    favoriteJob,
    fetchJobTemplates,
    removeSelectedJobs,
    setSelectedJobs
} from 'Src/ui-core/store/actions/setup-workboard';

const STAR_ICON_STYLE = {
    style: {
        height: '24px',
        width: '24px'
    }
};

const REMOVE_ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px'
    }
};

export function getJobFavoriteIcon(countLabel, job, dispatch) {
    const icon = job.isFavorite ? (
        <IconStar
            primary='#367C2B'
            {...STAR_ICON_STYLE}
        />
    ) : (
        <IconStarEmpty
            primary='#333'
            {...STAR_ICON_STYLE}
        />
    );

    return (
        <IconButton
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(
                    favoriteJob({
                        countLabel,
                        extraContent: getJobFavoriteIcon,
                        job
                    })
                );
            }}
            variant='borderless'
        >
            {icon}
        </IconButton>
    );
}

function WorkboardJobs(props) {
    const {
        handleFetchJobs,
        handleRemoveSelectedJob,
        handleSelectJob,
        jobs,
        loading,
        selectedJobs,
        openJobDialog,
        openJobsSettingsDialog,
        translations
    } = props;

    const dispatch = useDispatch();

    const [pendingSelectedIds, setPendingSelectedIds] = React.useState([]);

    const selectedJobIds = React.useMemo(() => {
        return selectedJobs.map((job) => job.id);
    }, [selectedJobs]);

    function onChange(newIds) {
        const isSelectingAll = newIds.length === jobs.length && selectedJobIds.length < jobs.length - 1;

        if (isSelectingAll) {
            setPendingSelectedIds(newIds);
            return;
        }

        handleSelectJob({
            jobs: getSelectedJobs(jobs, newIds)
        });
    }

    function confirmSelectAll() {
        handleSelectJob({
            jobs: getSelectedJobs(jobs, pendingSelectedIds)
        });
        setPendingSelectedIds([]);
    }

    function cancelSelectAll() {
        setPendingSelectedIds([]);
    }

    const addJobAction = (
        <Button
            onClick={() =>
                openJobDialog({
                    getUpdatedJobTemplates: (jobId) => {
                        handleFetchJobs({
                            addedJob: jobId,
                            dispatcher: dispatch,
                            extraContent: getJobFavoriteIcon,
                            operatorCountLabel:
                                translations.ONLINK_OPERATOR_COUNT
                        });
                    }
                })
            }
            startIcon={<IconAdd/>}
            title={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                '0': translations.jobs_job
            })}
            variant='borderless'
        >
            {replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                '0': translations.jobs_job
            })}
        </Button>
    );

    const jobsFooterAction =
        pendingSelectedIds.length > 0 ? (
            <SelectAllConfirmation
                onCancel={cancelSelectAll}
                onConfirm={confirmSelectAll}
                title={replaceTranslationNames(translations.ONLINK_SELECT_ALL_CONFIRMATION, {
                    '0': String(pendingSelectedIds.length),
                    '1': translations.JOBS
                })}
                translations={translations}
            />
        ) :
            addJobAction

        ;

    const totalOperatorCount = React.useMemo(() => {
        return getTotalOperatorCount(selectedJobs);
    }, [selectedJobs, jobs]);

    React.useEffect(() => {
        handleFetchJobs({
            dispatcher: dispatch,
            extraContent: getJobFavoriteIcon,
            operatorCountLabel: translations.ONLINK_OPERATOR_COUNT
        });
    }, []);

    function handleRemove(job) {
        handleRemoveSelectedJob({
            idsToRemove: [job.id],
            jobs: selectedJobs
        });
    }

    function getJobSecondaryAction(countLabel, job, dispatch) {
        const removeBtn = (
            <IconButton
                onClick={(e) => {
                    e.preventDefault();
                    handleRemove(job);
                }}
                variant='borderless'
            >
                <IconRemove
                    {...REMOVE_ICON_STYLE}
                    primary='#5E5E5D'
                />
            </IconButton>
        );
        const favoriteIcon = getJobFavoriteIcon(countLabel, job, dispatch);

        return (
            <Box
                alignItems={'center'}
                display={'flex'}
                gap={'8px'}
            >
                {favoriteIcon} {removeBtn}{' '}
            </Box>
        );
    }

    const linkProps = {
        actions: [
            {
                onClick: (e) => {
                    onLinkClick(e);
                    openJobsSettingsDialog({
                        getUpdatedJobTemplates: () =>
                            handleFetchJobs({
                                dispatcher: dispatch,
                                extraContent: getJobFavoriteIcon,
                                operatorCountLabel:
                                    translations.ONLINK_OPERATOR_COUNT
                            }),
                        translations
                    });
                }
            }
        ],
        message: translations.ONLINK_MANAGE_JOBS_TOOLTIP
    };
    const linkableText = useLinkableText(linkProps);

    return (
        <Stack
            data-id='workboard-jobs'
            direction='column'
            padding='16px'
        >
            <Stack
                alignItems='center'
                direction='row'
                marginBottom='16px'
                spacing={1}
            >
                <h3>{translations.JOBS}</h3>
                <AssistanceTooltip
                    body={
                        <Box padding='8px'>
                            <Typography
                                marginBottom='8px'
                                variant='h4'
                            >
                                {replaceTranslationNames(
                                    translations.MANAGE_PLACEHOLDER,
                                    {
                                        '0': translations.JOBS
                                    }
                                )}
                            </Typography>
                            <Stack
                                display='block'
                                sx={{
                                    '& a, & .MuiLink-root': {
                                        cursor: 'pointer !important'
                                    }
                                }}
                            >
                                {linkableText}
                            </Stack>
                        </Box>
                    }
                >
                    <IconInfoAlert size={16}/>
                </AssistanceTooltip>
            </Stack>
            <MultiSelect
                disableSorting
                enableVirtualization
                footerAction={jobsFooterAction}
                helperText={translations.ONLINK_SELECT_JOBS_TO_ASSIGN}
                items={jobs}
                labels={{
                    multiSelect: translations.ONLINK_JOBS_SELECTED,
                    placeholder: replaceTranslationNames(
                        translations.SELECT_PLACEHOLDER,
                        {
                            '0': translations.JOBS
                        }
                    ),
                    emptyList: translations.NO_ITEMS,
                    noItemMatch: translations.NO_RESULTS_FOUND,
                    allSelect: translations.ALL_SELECTED
                }}
                loading={loading}
                name='jobs'
                onChange={(selectedIds) => onChange(selectedIds)}
                onChangeOpen={(isOpen) => {
                    if (!isOpen) {
                        setPendingSelectedIds([]);
                    }
                }}
                selectedIds={selectedJobIds}
            />
            {selectedJobs.length > 0 && (
                <Box
                    border={'1px solid #b3b3b3'}
                    borderRadius={'4px'}
                    marginTop={'16px'}
                    overflow={'hidden'}
                >
                    <List disablePadding>
                        {selectedJobs.map((job, i) => (
                            <div key={job.id}>
                                <JobListItem
                                    job={job}
                                    loading={loading}
                                    secondaryAction={getJobSecondaryAction(
                                        translations.ONLINK_OPERATOR_COUNT,
                                        job,
                                        dispatch
                                    )}
                                    translations={translations}
                                />
                                {i < selectedJobs.length - 1 && (
                                    <Divider component='li'/>
                                )}
                            </div>
                        ))}
                    </List>
                    <Box
                        bgcolor='#C7C6C5'
                        display='flex'
                        fontWeight='bold'
                        justifyContent='flex-start'
                        p={2}
                        paddingLeft='16px'
                    >
                        <span>{`${translations.ONLINK_TOTAL_OPERATOR_COUNT}: ${loading ? '---' : totalOperatorCount}`}</span>
                    </Box>
                </Box>
            )}
        </Stack>
    );
}

WorkboardJobs.propTypes = {
    handleFetchJobs: PropTypes.func,
    handleRemoveSelectedJob: PropTypes.func,
    handleSelectJob: PropTypes.func,
    isMobile: PropTypes.bool,
    jobs: PropTypes.array,
    loading: PropTypes.bool,
    openJobDialog: PropTypes.func,
    openJobsSettingsDialog: PropTypes.func,
    selectedJobs: PropTypes.arrayOf(PropTypes.object),
    setSelectedJobs: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        jobs: state.setupWorkboard.jobs.jobs,
        loading: state.setupWorkboard.jobs.loading,
        selectedJobs: state.setupWorkboard.jobs.selectedJobs
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        handleFetchJobs(props) {
            dispatch(fetchJobTemplates(props));
        },
        handleRemoveSelectedJob(props) {
            dispatch(removeSelectedJobs(props));
        },
        handleSelectJob(props) {
            dispatch(setSelectedJobs(props));
        },
        openJobDialog(props) {
            dispatch(openDialog(dialogTypes.JOB_DIALOG, props));
        },
        openJobsSettingsDialog(props) {
            dispatch(openDialog(dialogTypes.JOBS_SETTINGS_DIALOG, props));
        }
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MediaQuery(MOBILE_MEDIA_QUERY)(WorkboardJobs));
